/**
 * Login Page Component
 * Hope SACCO Frontend - React + Firebase + Backend API
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import './LoginPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PENDING_KEY = 'pendingApprovalEmail';

// Logo hosted on Firebase Storage — paste the download URL you get from the
// Storage console (or REACT_APP_LOGO_URL in your .env) here instead of base64.
const LOGO_URL = process.env.REACT_APP_LOGO_URL || 'https://firebasestorage.googleapis.com/v0/b/hope-sacco-42375.firebasestorage.app/o/Jepros_Logo_dark_theme.png?alt=media&token=fc9e9e48-89b5-4bfc-8f0c-763483af816d';


const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ FIX: Track when a login is in progress so the "already logged in"
  // useEffect does not fire mid-login and cause the blink/redirect loop.
  const isLoggingInRef = useRef(false);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  // Check if already logged in (skip if currently logging in)
  useEffect(() => {
    // Don't redirect if login is already in progress
    if (isLoggingInRef.current) return;

    // Don't redirect if we just got kicked out due to session expiry —
    // the token may still be in localStorage during the redirect race.
    if (location.state?.sessionExpired) {
      localStorage.removeItem('hopeSaccoToken');
      localStorage.removeItem('hopeSaccoUser');
      return;
    }

    const token = localStorage.getItem('hopeSaccoToken');
    const user = localStorage.getItem('hopeSaccoUser');

    if (token && user && user !== 'undefined') {
      try {
        const userData = JSON.parse(user);
        const dashboardRoute = getDashboardRoute(userData.role);
        navigate(dashboardRoute);
      } catch (error) {
        localStorage.removeItem('hopeSaccoToken');
        localStorage.removeItem('hopeSaccoUser');
      }
    }
  }, [navigate, location.state]);

  // Read pending email from localStorage
  useEffect(() => {
    try {
      setPendingEmail((localStorage.getItem(PENDING_KEY) || '').trim().toLowerCase());
    } catch {
      setPendingEmail('');
    }
  }, []);

  // Set banner from navigation state (signup redirect or session expired)
  useEffect(() => {
    const state = location.state;

    if (state?.pending) {
      if (state?.justSignedUp) {
        setGeneralError('Account created. Your account must be approved by the admin before you can sign in.');
      } else {
        setGeneralError('Your account is still awaiting admin approval. Please wait for approval before signing in.');
      }

      const stEmail = typeof state?.email === 'string' ? state.email.trim().toLowerCase() : '';
      if (stEmail) {
        try {
          localStorage.setItem(PENDING_KEY, stEmail);
        } catch {}
        setPendingEmail(stEmail);
      }
    }

    // Show session-expired message if redirected from SessionMonitor
    if (state?.sessionExpired && state?.message) {
      setGeneralError(state.message);
      // Wipe the state from browser history so a page refresh doesn't re-show the message
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Show pending banner when typed email matches pendingEmail
  useEffect(() => {
    const typed = formData.email.trim().toLowerCase();

    if (generalError && pendingEmail) {
      const isPendingBanner =
        generalError.includes('awaiting admin approval') ||
        generalError.includes('must be approved by the admin') ||
        generalError.includes('not yet approved');

      if (isPendingBanner && typed && typed !== pendingEmail) {
        setGeneralError('');
      }
    }

    if (!generalError && pendingEmail && typed && typed === pendingEmail) {
      setGeneralError('Your account is still awaiting admin approval. Please wait for approval before signing in.');
    }
  }, [formData.email, pendingEmail, generalError]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDashboardRoute = (role) => {
    const routes = {
      admin: '/role-select',
      official: '/role-select',
      member: '/member/dashboard'
    };
    return routes[role] || '/member/dashboard';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const cleanEmail = formData.email.trim();
    const cleanEmailLower = cleanEmail.toLowerCase();

    setGeneralError('');
    setIsLoading(true);

    // ✅ FIX: Signal that login is in progress so the "already logged in"
    // useEffect doesn't fire and cause the blink.
    isLoggingInRef.current = true;

    try {
      console.log('🔐 Starting login process...');

      // Step 1: Authenticate with Firebase
      const userCredential = await signInWithEmailAndPassword(
        auth,
        cleanEmail,
        formData.password
      );
      console.log('✅ Firebase auth successful');

      // Get Firebase ID token (force refresh to ensure it's valid)
      const idToken = await userCredential.user.getIdToken(true);
      console.log('✅ Got Firebase token');

      // Step 2: Call backend API for validation
      console.log('📡 Calling backend API:', `${API_URL}/auth/login`);
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: cleanEmail,
          idToken: idToken
        })
      });

      console.log('📥 Backend response status:', response.status);
      const data = await response.json();
      console.log('📥 Backend response data:', data);

      if (!response.ok) {
        console.error('❌ Backend returned error:', data.error);
        throw new Error(data.error || 'Login failed');
      }

      if (data.success) {
        // Clear pending flag on successful login
        try {
          localStorage.removeItem(PENDING_KEY);
        } catch {}
        setPendingEmail('');

        // Store authentication data
        localStorage.setItem('hopeSaccoToken', idToken);
        localStorage.setItem('hopeSaccoUser', JSON.stringify(data.data));

        // Navigate to dashboard
        console.log('🔍 USER ROLE:', data.data.role);
        console.log('🔍 DASHBOARD ROUTE:', getDashboardRoute(data.data.role));
        const dashboardRoute = getDashboardRoute(data.data.role);

        // ✅ FIX: navigate BEFORE resetting isLoggingInRef so the redirect
        // useEffect cannot fire in between.
        navigate(dashboardRoute, {
          state: {
            message: `Welcome back, ${data.data.profile?.displayName || data.data.email}!`
          }
        });
      } else {
        // Check if it's an approval issue
        if (data.error && (
          data.error.includes('not approved') ||
          data.error.includes('awaiting approval') ||
          data.error.includes('inactive')
        )) {
          try {
            localStorage.setItem(PENDING_KEY, cleanEmailLower);
          } catch {}
          setPendingEmail(cleanEmailLower);
          setGeneralError('Your account is not yet approved by the admin. Please wait for approval before signing in.');
        } else {
          setGeneralError(data.error || 'Login failed');
        }
      }

    } catch (error) {
      console.error('Login error:', error);

      let errorMessage = 'Login failed. Please try again.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address format.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setGeneralError(errorMessage);
    } finally {
      setIsLoading(false);
      // ✅ FIX: Reset the flag after the full login attempt completes
      isLoggingInRef.current = false;
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  const [showLoginModal, setShowLoginModal] = useState(false);

  // Close modal on Escape key
  React.useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setShowLoginModal(false); };
    if (showLoginModal) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showLoginModal]);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactSubject, setContactSubject] = useState('');

  const openContactModal = (subject) => {
    setContactSubject(subject || '');
    setShowContactModal(true);
  };


  // Close contact modal on Escape key
  React.useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setShowContactModal(false); };
    if (showContactModal) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showContactModal]);

  return (
    <div className="st-root">
      {/* ── Google Fonts ── */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .st-root { font-family: 'Inter', sans-serif; background: #F8F5F0; color: #0D1B2A; }

        /* NAV */
        .st-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 6%; height: 68px;
          background: rgba(13,27,42,0.97); backdrop-filter: blur(8px); }
        .st-nav-logo { display: flex; align-items: center; gap: 10px; }
        .st-nav-logo span { font-family: 'Playfair Display', serif; font-size: 22px; color: #C9A84C; font-weight: 700; letter-spacing: 0.3px; }
        .st-nav-logo small { font-size: 11px; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; display: block; margin-top: -2px; }
        .st-nav-links { display: flex; gap: 32px; }
        .st-nav-links a { color: #cbd5e1; font-size: 14px; text-decoration: none; transition: color .2s; cursor: pointer; }
        .st-nav-links a:hover { color: #C9A84C; }
        .st-login-btn { background: #C9A84C; color: #0D1B2A; border: none; padding: 9px 24px;
          border-radius: 4px; font-size: 14px; font-weight: 600; cursor: pointer;
          transition: background .2s; }
        .st-login-btn:hover { background: #b8963e; }

        /* HERO */
        .st-hero { min-height: 100vh; background: #0D1B2A;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 120px 6% 80px; text-align: center; position: relative; overflow: hidden; }
        .st-hero::before { content: ''; position: absolute; top: -40%; left: -20%;
          width: 700px; height: 700px; border-radius: 50%;
          background: radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%); pointer-events: none; }
        .st-hero-logo { position: absolute; top: 96px; left: 6%; width: clamp(130px, 11vw, 190px);
          z-index: 2; }
        .st-hero-top { display: flex; justify-content: center;
          width: 100%; max-width: 1100px; margin: 0 auto 56px; }
        @media (max-width: 720px) {
          .st-hero-logo { position: static; margin: 0 auto 24px; display: block; }
        }
        .st-hero-eyebrow { font-size: 12px; letter-spacing: 3px; text-transform: uppercase;
          color: #C9A84C; margin-bottom: 24px; }
        .st-hero h1 { font-family: 'Playfair Display', serif; font-size: clamp(32px, 3.6vw, 56px);
          color: #F8F5F0; line-height: 1.15; margin-bottom: 24px; }
        .st-hero h1 em { color: #C9A84C; font-style: normal; }
        .st-hero-sub { font-size: 17px; color: #94a3b8; max-width: 520px; line-height: 1.7; margin-bottom: 48px; }
        .st-hero-cta { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; margin-bottom: 72px; }
        .st-cta-primary { background: #C9A84C; color: #0D1B2A; border: none; padding: 14px 36px;
          border-radius: 4px; font-size: 15px; font-weight: 600; cursor: pointer; transition: background .2s; }
        .st-cta-primary:hover { background: #b8963e; }
        .st-cta-secondary { background: transparent; color: #F8F5F0; border: 1px solid rgba(248,245,240,0.3);
          padding: 14px 36px; border-radius: 4px; font-size: 15px; cursor: pointer; transition: all .2s; }
        .st-cta-secondary:hover { border-color: #C9A84C; color: #C9A84C; }

        /* STAT STRIP */
        .st-stats { display: flex; gap: 0; border-top: 1px solid rgba(201,168,76,0.2);
          border-bottom: 1px solid rgba(201,168,76,0.2); padding: 28px 0; flex-wrap: wrap; justify-content: center; }
        .st-stat { padding: 0 48px; text-align: center; border-right: 1px solid rgba(201,168,76,0.15); }
        .st-stat:last-child { border-right: none; }
        .st-stat-num { font-family: 'Playfair Display', serif; font-size: 32px; color: #C9A84C; font-weight: 700; }
        .st-stat-label { font-size: 12px; color: #64748b; letter-spacing: 1.5px; text-transform: uppercase; margin-top: 4px; }

        /* FEATURES */
        .st-section { padding: 100px 6%; max-width: 1200px; margin: 0 auto; }
        .st-section-eyebrow { font-size: 12px; letter-spacing: 3px; text-transform: uppercase; color: #C9A84C; margin-bottom: 16px; }
        .st-section h2 { font-family: 'Playfair Display', serif; font-size: clamp(28px, 3vw, 42px); color: #0D1B2A; margin-bottom: 56px; max-width: 480px; line-height: 1.2; }
        .st-features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 2px; background: #e2d9cc; border: 2px solid #e2d9cc; border-radius: 8px; overflow: hidden; }
        .st-feature-card { background: #F8F5F0; padding: 40px 32px; transition: background .2s; }
        .st-feature-card:hover { background: #fff; }
        .st-feature-icon { font-size: 28px; margin-bottom: 20px; }
        .st-feature-card h3 { font-family: 'Playfair Display', serif; font-size: 20px; color: #0D1B2A; margin-bottom: 12px; font-weight: 600; }
        .st-feature-card p { font-size: 14px; color: #6B7280; line-height: 1.7; }

        /* HOW IT WORKS */
        .st-how { background: #0D1B2A; padding: 100px 6%; }
        .st-how-inner { max-width: 1200px; margin: 0 auto; }
        .st-how .st-section-eyebrow { color: #C9A84C; }
        .st-how h2 { color: #F8F5F0; }
        .st-how-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 40px; }
        .st-step { border-left: 2px solid #C9A84C; padding-left: 24px; }
        .st-step-num { font-family: 'Playfair Display', serif; font-size: 40px; color: rgba(201,168,76,0.3); font-weight: 700; line-height: 1; margin-bottom: 16px; }
        .st-step h3 { color: #F8F5F0; font-size: 17px; font-weight: 600; margin-bottom: 10px; }
        .st-step p { color: #64748b; font-size: 14px; line-height: 1.7; }

        /* FOOTER */
        .st-footer { background: #060d14; padding: 48px 6%; display: flex; flex-wrap: wrap;
          align-items: center; justify-content: space-between; gap: 24px; }
        .st-footer-brand span { font-family: 'Playfair Display', serif; font-size: 18px; color: #C9A84C; }
        .st-footer-brand p { font-size: 12px; color: #475569; margin-top: 6px; }
        .st-footer-contact { text-align: right; }
        .st-footer-contact p { font-size: 13px; color: #475569; line-height: 1.8; }
        .st-footer-contact a { color: #C9A84C; text-decoration: none; }

        /* MODAL */
        .st-modal-overlay { position: fixed; inset: 0; z-index: 999;
          background: rgba(6,13,20,0.85); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; padding: 24px; }
        .st-modal { background: #fff; border-radius: 8px; width: 100%; max-width: 420px;
          padding: 48px 40px; position: relative; box-shadow: 0 32px 80px rgba(0,0,0,0.4); }
        .st-modal-close { position: absolute; top: 16px; right: 18px; background: none; border: none;
          font-size: 20px; cursor: pointer; color: #94a3b8; line-height: 1; }
        .st-modal-close:hover { color: #0D1B2A; }
        .st-modal-header { text-align: center; margin-bottom: 32px; }
        .st-modal-header h2 { font-family: 'Playfair Display', serif; font-size: 26px; color: #0D1B2A; margin-bottom: 6px; }
        .st-modal-header p { font-size: 14px; color: #6B7280; }
        .st-modal .login-form .form-group { margin-bottom: 20px; }
        .st-modal .login-form label { display: block; font-size: 13px; font-weight: 500; color: #374151; margin-bottom: 6px; }
        .st-modal .login-form input { width: 100%; padding: 10px 14px; border: 1px solid #d1d5db;
          border-radius: 4px; font-size: 14px; outline: none; transition: border .2s; }
        .st-modal .login-form input:focus { border-color: #C9A84C; }
        .st-modal .login-form input.input-error { border-color: #ef4444; }
        .st-modal .login-form textarea { width: 100%; padding: 10px 14px; border: 1px solid #d1d5db;
          border-radius: 4px; font-size: 14px; outline: none; transition: border .2s; font-family: inherit; resize: vertical; }
        .st-modal .login-form textarea:focus { border-color: #C9A84C; }
        .st-modal .error-message { font-size: 12px; color: #ef4444; margin-top: 4px; display: block; }
        .st-modal .password-input-wrapper { position: relative; }
        .st-modal .password-input-wrapper input { padding-right: 44px; }
        .st-modal .password-toggle { position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer; font-size: 16px; }
        .st-modal .btn-primary { width: 100%; padding: 12px; background: #0D1B2A; color: #fff;
          border: none; border-radius: 4px; font-size: 15px; font-weight: 600; cursor: pointer;
          transition: background .2s; margin-top: 4px; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .st-modal .btn-primary:hover:not(:disabled) { background: #1a2f48; }
        .st-modal .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .st-modal .alert { padding: 12px 16px; border-radius: 4px; font-size: 13px; margin-bottom: 20px; display: flex; gap: 8px; align-items: flex-start; }
        .st-modal .alert-error { background: #fef2f2; color: #991b1b; }
        .st-modal .alert-info { background: #eff6ff; color: #1e40af; }
        .st-modal-footer { text-align: center; margin-top: 20px; display: flex; flex-direction: column; gap: 12px; }
        .st-modal-footer .link-button { background: none; border: none; color: #6B7280; font-size: 13px; cursor: pointer; text-decoration: underline; }
        .st-modal-footer .link-button:hover { color: #0D1B2A; }
        .st-modal .spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; display: inline-block; }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 640px) {
          .st-nav-links { display: none; }
          .st-stat { padding: 16px 24px; }
          .st-modal { padding: 36px 24px; }
        }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav className="st-nav">
        <div className="st-nav-logo">
          <div>
            <span>SaccoTeck</span>
            <small>by Jepros Teck</small>
          </div>
        </div>
        <div className="st-nav-links">
          <a onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>Features</a>
          <a onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>How it works</a>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <a onClick={() => openContactModal('General Inquiry')}
            style={{ color: '#94a3b8', fontSize: 14, textDecoration: 'none', transition: 'color .2s', cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.color='#C9A84C'}
            onMouseLeave={e => e.target.style.color='#94a3b8'}>
            📧 Contact Us
          </a>
          <a onClick={() => openContactModal('Call')}
            style={{ color: '#94a3b8', fontSize: 14, textDecoration: 'none', transition: 'color .2s', cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.color='#C9A84C'}
            onMouseLeave={e => e.target.style.color='#94a3b8'}>
            📞 Call
          </a>
          <button className="st-login-btn" onClick={() => setShowLoginModal(true)}>Login</button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="st-hero">
        {/* Logo — pinned top-left, out of the text flow so it doesn't push the heading around */}
        <img src={LOGO_URL}
          alt="Jepros Teck"
          className="st-hero-logo" />
        <div className="st-hero-top">
          <div style={{ maxWidth: 720, textAlign: 'left' }}>
            <p className="st-hero-eyebrow">SACCO Management Software</p>
            <h1>Run your SACCO with <em>clarity</em> and confidence</h1>
            <p className="st-hero-sub">
              SaccoTeck gives your team a single system to manage members, track loans, process monthly deductions, and generate financial reports — all in one place.
            </p>
            <div className="st-hero-cta" style={{ justifyContent: 'flex-start' }}>
              <button className="st-cta-primary" onClick={() => setShowLoginModal(true)}>Login to your SACCO</button>
              <button className="st-cta-secondary" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>See how it works</button>
            </div>
          </div>
        </div>
        <div className="st-stats">
          <div className="st-stat"><div className="st-stat-num">200+</div><div className="st-stat-label">Members Managed</div></div>
          <div className="st-stat"><div className="st-stat-num">5</div><div className="st-stat-label">Loan Types</div></div>
          <div className="st-stat"><div className="st-stat-num">KES 100M+</div><div className="st-stat-label">Assets Tracked</div></div>
          <div className="st-stat"><div className="st-stat-num">100%</div><div className="st-stat-label">Cloud-Based</div></div>
        </div>
      </section>


      {/* ── FEATURES ── */}
      <div id="features" style={{ background: '#F8F5F0', padding: '100px 0' }}>
        <div className="st-section" style={{ padding: '0 6%' }}>
          <p className="st-section-eyebrow">What SaccoTeck does</p>
          <h2>Everything your SACCO needs, built in</h2>
          <div className="st-features-grid">
            <div className="st-feature-card">
              <div className="st-feature-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div>
              <h3>Member Management</h3>
              <p>Register members, track their profile, shares, and loan history. Staff and Workforce categories supported.</p>
            </div>
            <div className="st-feature-card">
              <div className="st-feature-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg></div>
              <h3>Loan Processing</h3>
              <p>Handle normal, school fees, and emergency loans. Automatic interest calculation at 1% per month on reducing balance.</p>
            </div>
            <div className="st-feature-card">
              <div className="st-feature-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v2m0 8v2M9 9.5h4.5a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3H15"/></svg></div>
              <h3>Shares & Contributions</h3>
              <p>Track monthly share contributions and deposits. Automatic plan backfill and borrowing cap calculation.</p>
            </div>
            <div className="st-feature-card">
              <div className="st-feature-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><polyline points="7 10 10 7 13 10 17 6"/></svg></div>
              <h3>Monthly Processing</h3>
              <p>Generate monthly deduction schedules for all members. Export to Excel for payroll. Save and lock records.</p>
            </div>
            <div className="st-feature-card">
              <div className="st-feature-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg></div>
              <h3>Member Statements</h3>
              <p>Per-member statements showing every deduction, loan balance, and share balance month by month.</p>
            </div>
            <div className="st-feature-card">
              <div className="st-feature-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/><path d="M2 20h20"/></svg></div>
              <h3>Financial State</h3>
              <p>SACCO-wide financial overview showing total assets, income, expenses, and trends over time.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div id="how-it-works" className="st-how">
        <div className="st-how-inner">
          <p className="st-section-eyebrow">The workflow</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 3vw, 42px)', color: '#F8F5F0', marginBottom: 56, maxWidth: 480, lineHeight: 1.2 }}>
            From registration to financial report
          </h2>
          <div className="st-how-steps">
            <div className="st-step">
              <div className="st-step-num">01</div>
              <h3>Register Members</h3>
              <p>Add members with their personal details, assign Member IDs, and set up their monthly share contribution plan.</p>
            </div>
            <div className="st-step">
              <div className="st-step-num">02</div>
              <h3>Process Loans</h3>
              <p>Apply and approve normal, school fees, or emergency loans. The system tracks repayment schedules automatically.</p>
            </div>
            <div className="st-step">
              <div className="st-step-num">03</div>
              <h3>Monthly Processing</h3>
              <p>Each month, run Monthly Processing to generate deduction figures for all members. Adjust where needed and export to payroll.</p>
            </div>
            <div className="st-step">
              <div className="st-step-num">04</div>
              <h3>Review Reports</h3>
              <p>Check member statements, shares reports, loans outstanding, and the overall SACCO financial state at any time.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── ASK FOR DEMO ── */}
      <div style={{ background: '#F8F5F0', borderTop: '1px solid #e2d9cc', padding: '80px 6%', textAlign: 'center' }}>
        <p style={{ fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: '#C9A84C', marginBottom: 16 }}>See it in action</p>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px, 3vw, 38px)', color: '#0D1B2A', marginBottom: 16, lineHeight: 1.2 }}>
          Want a demo for your SACCO?
        </h2>
        <p style={{ fontSize: 15, color: '#6B7280', maxWidth: 460, margin: '0 auto 36px', lineHeight: 1.7 }}>
          We'll walk you through the full system and show you how SaccoTeck can work for your organization.
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a onClick={() => openContactModal('SaccoTeck Demo Request')}
            style={{ background: '#0D1B2A', color: '#fff', padding: '13px 32px', borderRadius: 4,
              fontSize: 14, fontWeight: 600, textDecoration: 'none', display: 'inline-block', transition: 'background .2s', cursor: 'pointer' }}
            onMouseEnter={e => e.target.style.background='#1a2f48'}
            onMouseLeave={e => e.target.style.background='#0D1B2A'}>
            ✉️ Request a Demo
          </a>
          <a onClick={() => openContactModal('Call')}
            style={{ background: 'transparent', color: '#0D1B2A', padding: '13px 32px', borderRadius: 4,
              fontSize: 14, fontWeight: 600, textDecoration: 'none', border: '1px solid #0D1B2A', display: 'inline-block', cursor: 'pointer' }}>
            📞 Call Us
          </a>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="st-footer">
        <div className="st-footer-brand">
          <span>SaccoTeck</span>
          <p>© {new Date().getFullYear()} Jepros Teck · Cable Systems Interprise</p>
        </div>
        <div className="st-footer-contact">
          <p>📧 jeprosservices@gmail.com</p>
          <p style={{ marginTop: 4 }}>Built for Hope Savings & Credit Co-operative Society</p>
          <p style={{ marginTop: 4, display: 'flex', gap: 12 }}>
            <span
              onClick={() => navigate('/privacy-policy')}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Privacy Policy
            </span>
            <span
              onClick={() => navigate('/delete-account')}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Delete Account
            </span>
          </p>
        </div>
      </footer>

      {/* ── LOGIN MODAL ── */}
      {showLoginModal && (
        <div className="st-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowLoginModal(false); }}>
          <div className="st-modal">
            <button className="st-modal-close" onClick={() => setShowLoginModal(false)}>✕</button>
            <div className="st-modal-header">
              <h2>Welcome back</h2>
              <p>Sign in to your SACCO account</p>
            </div>

            {generalError && (
              <div className={`alert ${
                generalError.includes('awaiting') || generalError.includes('must be approved') || generalError.includes('🔒')
                  ? 'alert-info' : 'alert-error'
              }`}>
                <span>{generalError.includes('awaiting') || generalError.includes('must be approved') ? 'ℹ️' : '⚠️'}</span>
                <span>{generalError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="email">Email Address <span style={{color:'#ef4444'}}>*</span></label>
                <input type="email" id="email" name="email" value={formData.email}
                  onChange={handleChange} className={errors.email ? 'input-error' : ''}
                  placeholder="Enter your email" disabled={isLoading} autoComplete="email" autoFocus />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="password">Password <span style={{color:'#ef4444'}}>*</span></label>
                <div className="password-input-wrapper">
                  <input type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange}
                    className={errors.password ? 'input-error' : ''}
                    placeholder="Enter your password" disabled={isLoading} autoComplete="current-password" />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} disabled={isLoading}>
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (<><span className="spinner"></span> Signing in...</>) : 'Sign In'}
              </button>
            </form>

            <div className="st-modal-footer">
              <button type="button" onClick={() => { setShowLoginModal(false); navigate('/forgot-password'); }} className="link-button">
                Forgot password?
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTACT MODAL — simple info panel, no app dialogs ── */}
      {showContactModal && (
        <div className="st-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowContactModal(false); }}>
          <div className="st-modal" style={{ maxWidth: 400 }}>
            <button className="st-modal-close" onClick={() => setShowContactModal(false)}>✕</button>
            <div className="st-modal-header">
              <div style={{ fontSize: 36, marginBottom: 8 }}>
                {contactSubject?.includes('Demo') ? '🖥️' : '👋'}
              </div>
              <h2>{contactSubject?.includes('Demo') ? 'Request a Demo' : 'Contact Us'}</h2>
              <p style={{ color: '#6B7280', fontSize: 14 }}>
                {contactSubject?.includes('Demo')
                  ? "Reach out and we'll set up a walkthrough of SaccoTeck for your SACCO."
                  : "We'd love to hear from you. Reach us through any of the channels below."}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
              {/* Email */}
              <div style={{ background: '#F8F5F0', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 24 }}>📧</span>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Email</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0D1B2A', userSelect: 'all' }}>jeprosservices@gmail.com</div>
                </div>
              </div>

              {/* Phone */}
              <div style={{ background: '#F8F5F0', borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 24 }}>📞</span>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Call or WhatsApp</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0D1B2A', userSelect: 'all' }}>+254 700 000 000</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Mon – Fri, 8am – 6pm EAT</div>
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 20 }}>
              © {new Date().getFullYear()} Jepros Teck · Cable Systems Interprise
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;